import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Rule as RuleIcon,
  MoreVert as MoreIcon,
  Warning as WarningIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { StatusChip } from '../../../../../components/shared';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import { WebsitesTableProps, Website } from '../types';

/**
 * WebsitesTable - Displays the list of monitored websites.
 *
 * Features:
 * - Header with "Add Website" button
 * - Table with columns: Name, URL, Status, Alerts, Response Time, Uptime, Actions
 * - URL displayed in monospace with primary color
 * - Uptime shown as progress bar with color coding
 * - Quick action buttons: View, Manage Alerts, More Actions
 * - Helper tip at the bottom
 */
export function WebsitesTable({
  websites,
  onMenuOpen,
  onAddWebsite,
  onOpenAlertsDrawer,
  onWebsiteClick,
}: WebsitesTableProps) {
  const isMobile = useIsMobile();
  const theme = useTheme();

  /**
   * Calculate alert count based on status (simulated).
   * In production, this would come from actual alert data.
   */
  const getAlertCount = (website: Website): number => {
    return website.status === 'down' ? 1 : 0;
  };

  /**
   * Get uptime progress bar color based on percentage.
   */
  const getUptimeColor = (
    uptime: number
  ): 'success' | 'warning' | 'error' => {
    if (uptime >= 99) return 'success';
    if (uptime >= 95) return 'warning';
    return 'error';
  };

  return (
    <Box>
      {/* Header with Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Websites
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={onAddWebsite}
        >
          Add Website
        </Button>
      </Box>

      {/* Mobile Card View / Desktop Table View */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {websites.map((website) => {
            const alertCount = getAlertCount(website);
            return (
              <Paper key={website.id} elevation={0} onClick={() => onWebsiteClick?.(website)} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, cursor: onWebsiteClick ? 'pointer' : undefined, ...(website.enabled === false ? { opacity: 0.5 } : undefined) }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">{website.name}</Typography>
                  {website.enabled === false ? (
                    <Chip label="Paused" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
                  ) : (
                    <StatusChip status={website.status} />
                  )}
                </Box>
                <Typography variant="caption" color="primary" fontFamily="monospace" sx={{ display: 'block', mb: 1 }}>{website.url}</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                  {website.responseTime ? (
                    <Typography variant="caption">{website.responseTime}ms</Typography>
                  ) : (
                    <Typography variant="caption" color="error">-</Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LinearProgress variant="determinate" value={website.uptime} sx={{ width: 40, height: 4, borderRadius: 2 }} color={website.uptime >= 99 ? 'success' : website.uptime >= 95 ? 'warning' : 'error'} />
                    <Typography variant="caption">{website.uptime}%</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {alertCount > 0 ? (
                    <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={`${alertCount} active`} size="small" color="error" variant="outlined" />
                  ) : (
                    <Chip label="No alerts" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWebsiteClick?.(website); }}><ViewIcon /></IconButton>
                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer?.(website); }}><RuleIcon /></IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenuOpen(e, 'website', website); }}><MoreIcon /></IconButton>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <TableCell>Name</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Alerts</TableCell>
                <TableCell>Response Time</TableCell>
                <TableCell>Uptime</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {websites.map((website) => {
                const alertCount = getAlertCount(website);

                return (
                  <TableRow key={website.id} hover sx={{ ...(website.enabled === false ? { opacity: 0.5 } : undefined), cursor: onWebsiteClick ? 'pointer' : undefined }} onClick={() => onWebsiteClick?.(website)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {website.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color="primary"
                      >
                        {website.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {website.enabled === false ? (
                        <Chip label="Paused" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
                      ) : (
                        <StatusChip status={website.status} />
                      )}
                    </TableCell>
                    <TableCell>
                      {alertCount > 0 ? (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label={`${alertCount} active`}
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => onOpenAlertsDrawer?.(website)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Chip
                          label="No alerts"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {website.responseTime ? (
                        <Typography variant="body2">
                          {website.responseTime}ms
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="error">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={website.uptime}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                          color={getUptimeColor(website.uptime)}
                        />
                        <Typography variant="body2">{website.uptime}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWebsiteClick?.(website); }}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Alerts & Subscribers">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer?.(website); }}
                        >
                          <RuleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onMenuOpen(e, 'website', website); }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {websites.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <LanguageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No websites configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a website to start monitoring uptime and response times.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddWebsite}>
            Add Your First Website
          </Button>
        </Box>
      )}

      {/* Helper Tip */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        Click the{' '}
        <RuleIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> icon to
        manage alert rules, or use the menu for more options
      </Typography>
    </Box>
  );
}

export default WebsitesTable;
